import { Info } from 'lucide-react';
import { cloneElement, useId } from 'react';
import type { ReactNode } from 'react';

import { Button } from '../../ui/button';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../ui/hover-card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { ht } from './messages';

export function Hint(props: {
  label: string;
  children: ReactNode;
  description?: ReactNode;
}): ReactNode {
  // Radix wires the Tooltip branch's `aria-describedby`/`role="tooltip"`
  // itself; HoverCard is a plain floating panel and wires neither, so the
  // two branches otherwise expose different semantics for one affordance.
  const descriptionId = useId();
  const trigger = (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      aria-label={ht('about', { label: props.label })}
    >
      <Info aria-hidden />
    </Button>
  );
  if (props.description !== undefined) {
    return (
      <HoverCard openDelay={100}>
        <HoverCardTrigger asChild>
          {cloneElement(trigger, { 'aria-describedby': descriptionId })}
        </HoverCardTrigger>
        <HoverCardContent id={descriptionId} role="group" className="flex flex-col gap-2">
          <p className="text-label">{props.children}</p>
          <div className="text-body-sm text-muted-foreground">{props.description}</div>
        </HoverCardContent>
      </HoverCard>
    );
  }
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent>{props.children}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
